// The task-group page's Resources card (D83): the size of the group's environment, the owner's
// limit and where it comes from, and sliders to resize it within floor..limit. Nothing is
// rebuilt: the API applies the size live where Incus allows it, else restarts the VM now
// (`restarting`) or after the current task (`pending`). The card refetches with the group's
// live updates, and polls while a resize is still under way.
import { ArrowClockwise, Cpu, HourglassMedium } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import {
  environmentLimitsQuery,
  type EnvironmentLimits,
  fromGib,
  myLimitsQuery,
  resizeEnvironment,
  type Size,
  toGib,
} from "../../admin/resource-limits/api";
import { RangeField } from "../../admin/resource-limits/SizeFields";
import { accessQuery } from "../../auth/access";
import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { CollapsibleCard } from "../../ui/CollapsibleCard";
import { ErrorState, Loading } from "../../ui/states";
import { Chip, MicroBar, PulseDot, SummaryRow } from "../../ui/summary";
import type { TaskGroup } from "../model";
import { resourcesSummary } from "./summaries";

const RESIZING_POLL_MS = 5_000;

function StateBanner({ env }: { env: EnvironmentLimits }) {
  const t = useT().limits.card;
  if (env.limits_state === "applied") return null;
  const restarting = env.limits_state === "restarting";
  const Icon = restarting ? ArrowClockwise : HourglassMedium;
  return (
    <div role="status" className="mb-4 flex items-start gap-2.5 rounded-lg border border-blue/30 bg-blue-lighter/60 px-3 py-2.5 text-[13px] text-blue-dark">
      <Icon weight="bold" className={restarting ? "mt-0.5 h-4 w-4 shrink-0 animate-spin" : "mt-0.5 h-4 w-4 shrink-0"} aria-hidden />
      <div>
        <div className="font-medium">{restarting ? t.restarting : t.pending}</div>
        {restarting ? <div className="mt-0.5 text-blue-dark/80">{t.restartingVpn}</div> : null}
      </div>
    </div>
  );
}

function Resize({ group, env, limit, floor }: { group: TaskGroup; env: EnvironmentLimits; limit: Size; floor: Size }) {
  const { limits: t } = useT();
  const queryClient = useQueryClient();
  const maxCpus = Math.max(floor.cpus, limit.cpus);
  const maxGib = Math.max(toGib(floor.memory_bytes), toGib(limit.memory_bytes));
  const clampCpus = (n: number) => Math.min(maxCpus, Math.max(floor.cpus, n));
  const clampGib = (n: number) => Math.min(maxGib, Math.max(toGib(floor.memory_bytes), n));
  const [cpus, setCpus] = useState(clampCpus(env.limits_cpu));
  const [gib, setGib] = useState(clampGib(toGib(env.limits_memory_bytes)));
  const [busy, setBusy] = useState(false);
  const above = env.limits_cpu > limit.cpus || env.limits_memory_bytes > limit.memory_bytes;
  const changed = cpus !== env.limits_cpu || gib !== toGib(env.limits_memory_bytes);

  async function apply() {
    setBusy(true);
    try {
      const next = await resizeEnvironment(env.id, { cpus, memory_bytes: fromGib(gib) });
      queryClient.setQueryData(environmentLimitsQuery(group.id, env.id).queryKey, next);
      if (next.limits_state === "applied") toast(t.card.applied);
    } catch (e) {
      toast(`${t.card.notApplied}: ${e instanceof Error ? e.message : String(e)}`, "warn");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {above ? <p className="text-[13px] text-orange-dark">{t.card.aboveLimit}</p> : null}
      <RangeField label={t.cpus} value={cpus} min={floor.cpus} max={maxCpus} onChange={setCpus} />
      <RangeField label={t.memory} value={gib} min={toGib(floor.memory_bytes)} max={maxGib} unit="GiB" onChange={setGib} />
      <div className="flex justify-end">
        <Button size="sm" onClick={() => void apply()} isLoading={busy} disabled={!changed || env.limits_state === "restarting"}>
          {t.card.apply}
        </Button>
      </div>
    </div>
  );
}

function Summary({ env, limit }: { env: EnvironmentLimits; limit: Size }) {
  const { limits, tasks } = useT();
  const c = tasks.cards.resources;
  const s = resourcesSummary(env, limit);
  return (
    <SummaryRow>
      {s.busy ? <PulseDot tone="blue" label={s.busy === "restarting" ? c.restarting : c.pending} /> : null}
      <Chip tone="primary">{limits.size(s.cpus, s.gib)}</Chip>
      <MicroBar pct={Math.max(s.cpuPct, s.memPct)} label={c.usage(s.cpuPct, s.memPct)} />
    </SummaryRow>
  );
}

/** Shown to the group's owner once the group has an environment. */
export function ResourcesCard({ group }: { group: TaskGroup }) {
  const { limits: t } = useT();
  const me = useQuery(accessQuery()).data;
  const envId = group.environment_id ?? "";
  const show = me?.id === group.owner_user_id && envId !== "";
  const mine = useQuery({ ...myLimitsQuery(), enabled: show });
  const env = useQuery({
    ...environmentLimitsQuery(group.id, envId),
    enabled: show,
    refetchInterval: (q) => (q.state.data && q.state.data.limits_state !== "applied" ? RESIZING_POLL_MS : false),
  });
  if (!show) return null;
  const failed = mine.error ?? env.error;
  const sized = (cpus: number, bytes: number) => t.size(cpus, toGib(bytes));

  return (
    <CollapsibleCard
      kind="resources"
      icon={<Cpu weight="duotone" />}
      title={t.card.title}
      description={t.card.description}
      summary={mine.data && env.data ? <Summary env={env.data} limit={mine.data} /> : null}
    >
      {failed ? <ErrorState title={t.card.loadFailed} message={failed.message} /> : null}
      {!failed && (!mine.data || !env.data) ? <Loading /> : null}
      {mine.data && env.data ? (
        <>
          <StateBanner env={env.data} />
          <dl className="mb-4 grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wider text-gray-500">{t.card.current}</dt>
              <dd className="tabular mt-1 text-sm font-medium text-gray-900" data-testid="resources-current">
                {sized(env.data.limits_cpu, env.data.limits_memory_bytes)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wider text-gray-500">{t.card.limit}</dt>
              <dd className="tabular mt-1 text-sm font-medium text-gray-900" data-testid="resources-limit">
                {sized(mine.data.cpus, mine.data.memory_bytes)}
              </dd>
              <dd className="text-xs text-gray-500">{t.card.from(t.card.source[mine.data.source])}</dd>
            </div>
          </dl>
          {/* Keyed by the sizes, so a change made elsewhere (another tab, a lowered limit) resets the sliders. */}
          <Resize
            key={[env.data.limits_cpu, env.data.limits_memory_bytes, mine.data.cpus, mine.data.memory_bytes].join("/")}
            group={group}
            env={env.data}
            limit={mine.data}
            floor={mine.data.floor[group.isolation === "vm" ? "vm" : "container"]}
          />
        </>
      ) : null}
    </CollapsibleCard>
  );
}
